import React from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  PlayIcon,
  QuestionMarkCircleIcon,
  AcademicCapIcon,
  BugAntIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline'

const supportCategories = [
  {
    name: 'Getting Started',
    description: 'Everything you need to know to start finding leads on Reddit',
    icon: AcademicCapIcon,
    resources: [
      'Quick Start Guide',
      'Reddit Account Setup',
      'First Project Creation',
      'Understanding Reddit Subreddits'
    ]
  },
  {
    name: 'Features & Tutorials',
    description: 'Learn how to use every feature to maximize your results',
    icon: BookOpenIcon,
    resources: [
      'AI Lead Discovery',
      'Comment Generation',
      'Analytics Dashboard',
      'Notification Setup'
    ]
  },
  {
    name: 'Troubleshooting',
    description: 'Solutions to common issues and technical problems',
    icon: BugAntIcon,
    resources: [
      'Reddit API Issues',
      'Account Bans Prevention',
      'Performance Optimization',
      'Integration Problems'
    ]
  },
  {
    name: 'Best Practices',
    description: 'Proven strategies from successful Redd Miner users',
    icon: LightBulbIcon,
    resources: [
      'Reddit Marketing Ethics',
      'Conversion Optimization',
      'Community Building',
      'Content Strategy'
    ]
  }
]

const tutorials = [
  {
    title: 'Getting Started with Redd Miner',
    duration: '5 min',
    description: 'Complete walkthrough from signup to your first lead',
    type: 'video'
  },
  {
    title: 'Setting Up Reddit API Credentials',
    duration: '3 min',
    description: 'Step-by-step guide to connect your Reddit accounts',
    type: 'video'
  },
  {
    title: 'Creating High-Converting Comments',
    duration: '8 min',
    description: 'Best practices for authentic Reddit engagement',
    type: 'video'
  },
  {
    title: 'Advanced Keyword Strategy',
    duration: '12 min',
    description: 'Find the perfect keywords for maximum lead discovery',
    type: 'video'
  },
  {
    title: 'Analytics & ROI Tracking',
    duration: '6 min',
    description: 'Measure your success and optimize performance',
    type: 'video'
  },
  {
    title: 'Avoiding Reddit Bans',
    duration: '10 min',
    description: 'Essential safety tips for long-term success',
    type: 'video'
  }
]

const contactOptions = [
  {
    name: 'Email Us',
    description: 'Questions, feedback, or just want to chat? We read everything personally',
    icon: EnvelopeIcon,
    contact: 'support@reddminer.com',
    responseTime: 'Usually within a few hours',
    cta: 'Send Email'
  },
  {
    name: 'In-App Chat',
    description: 'Got an account? Use the built-in chat for quick help while you work',
    icon: ChatBubbleLeftRightIcon,
    contact: 'Available after login',
    responseTime: 'Pretty fast!',
    cta: 'Start Chat'
  }
]

const commonIssues = [
  {
    issue: 'Reddit API credentials not working',
    solution: 'Make sure your Reddit app is set to "script" type and you\'re using the correct credentials. Check our setup guide for detailed instructions.',
    category: 'Setup'
  },
  {
    issue: 'Not finding enough leads',
    solution: 'Try expanding your keyword list and monitoring more subreddits. Our AI gets better over time as it learns your preferences.',
    category: 'Performance'
  },
  {
    issue: 'Comments getting removed or banned',
    solution: 'Review our best practices guide. Make sure your comments add genuine value and follow each subreddit\'s rules.',
    category: 'Safety'
  },
  {
    issue: 'Dashboard not loading properly',
    solution: 'Clear your browser cache and cookies. If the problem persists, try a different browser or contact support.',
    category: 'Technical'
  }
]

const SupportPage = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative isolate px-6 pt-14 lg:px-8">
        <div className="mx-auto max-w-4xl py-24 sm:py-32">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl"
            >
              Need Help?
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                We've Got Your Back
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-6 text-xl leading-8 text-gray-600 max-w-3xl mx-auto"
            >
              Quick guides, common fixes, and real humans ready to help.
              We want you to succeed with Reddit marketing - seriously!
            </motion.p>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-gray-50 py-16">
        <div className="mx-auto max-w-4xl px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What can we help you with?</h2>
            <div className="relative">
              <input
                type="text"
                placeholder="Search for tutorials, guides, or common issues..."
                className="w-full px-6 py-4 text-lg border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2 bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors">
                Search
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Support Categories */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Browse by Category
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {supportCategories.map((category, index) => (
              <motion.div
                key={category.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-200 rounded-lg p-8 hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div className="flex items-center mb-4">
                  <category.icon className="h-8 w-8 text-blue-600 mr-3" />
                  <h3 className="text-xl font-semibold text-gray-900">{category.name}</h3>
                </div>
                <p className="text-gray-600 mb-6">{category.description}</p>
                <ul className="space-y-2">
                  {category.resources.map((resource, i) => (
                    <li key={i} className="text-blue-600 hover:text-blue-800 cursor-pointer">
                      • {resource}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Video Tutorials */}
      <div className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Video Tutorials
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Step-by-step video guides to master Reddit marketing
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {tutorials.map((tutorial, index) => (
              <motion.div
                key={tutorial.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
              >
                <div className="aspect-video bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
                  <PlayIcon className="h-16 w-16 text-white" />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded">
                      {tutorial.duration}
                    </span>
                    <span className="text-gray-500 text-xs uppercase">{tutorial.type}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{tutorial.title}</h3>
                  <p className="text-gray-600 text-sm">{tutorial.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Common Issues */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Common Issues & Solutions
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Quick fixes for the most frequently reported problems
            </p>
          </div>

          <div className="space-y-6">
            {commonIssues.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gray-50 rounded-lg p-6"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">{item.issue}</h3>
                  <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-1 rounded">
                    {item.category}
                  </span>
                </div>
                <p className="text-gray-700">{item.solution}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Support */}
      <div className="bg-blue-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Stuck? We're Here! 🚀
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Real people, real help. No bots, no scripts, just us.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {contactOptions.map((option, index) => (
              <motion.div
                key={option.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg p-8 shadow-lg text-center"
              >
                <option.icon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{option.name}</h3>
                <p className="text-gray-600 mb-4">{option.description}</p>
                <div className="text-sm text-gray-500 mb-6">
                  <div className="font-medium">{option.contact}</div>
                  <div>{option.responseTime}</div>
                </div>
                <Link
                  to="/contact"
                  className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  {option.cta}
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Success Stories */}
      <div className="py-24 sm:py-32">
        <div className="mx-auto max-w-4xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl mb-8">
            Real Results from Real People
          </h2>
          <p className="text-lg text-gray-600 mb-12">
            Check out what folks are building with Redd Miner
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-green-50 rounded-lg p-8"
            >
              <div className="text-2xl font-bold text-green-600 mb-2">$15k MRR</div>
              <div className="text-lg font-semibold text-gray-900 mb-4">Indie Hacker Win</div>
              <p className="text-gray-700 italic mb-4">
                "Went from manually scrolling Reddit 3 hours daily to finding leads on autopilot.
                Now I actually have time to build my product!"
              </p>
              <div className="text-sm font-medium">Alex P. - Solo Founder</div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-blue-50 rounded-lg p-8"
            >
              <div className="text-2xl font-bold text-blue-600 mb-2">50+ Leads/Week</div>
              <div className="text-lg font-semibold text-gray-900 mb-4">Agency Growth</div>
              <p className="text-gray-700 italic mb-4">
                "Reddit was always great for leads but took forever. Redd Miner made it our
                #1 source without the manual grind."
              </p>
              <div className="text-sm font-medium">Jamie K. - Small Agency</div>
            </motion.div>
          </div>

          <div className="mt-12">
            <Link
              to="/signup"
              className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-lg transition-all duration-200 hover:scale-105"
            >
              Try It Free - No Credit Card
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SupportPage